import React from 'react';
import { useTranslation } from 'react-i18next';

export interface ContentMasterReviewFooterProps {
  /** Re-fetch the current tab/list. */
  onRefresh: () => void;
  /** Disables the refresh button while a fetch is in flight. */
  refreshing?: boolean;
  /** Primary CTA label (apply / retry all). Omit to render the refresh button only. */
  primaryLabel?: string | null;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  /** Tooltip on the primary CTA (e.g. subuser without permission). */
  primaryTitle?: string;
  /** Optional secondary CTA (link-styled), rendered before the primary one. */
  secondaryLabel?: string | null;
  onSecondary?: () => void;
  secondaryDisabled?: boolean;
}

/**
 * Fixed bottom toolbar shared by the Content Master and Marketplace listing
 * review pages. Always shows a Refresh button; the optional primary CTA is the
 * per-tab action (apply on the Ready tab, retry on the Errors tab).
 */
const ContentMasterReviewFooter: React.FC<ContentMasterReviewFooterProps> = ({
  onRefresh,
  refreshing = false,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryTitle,
  secondaryLabel,
  onSecondary,
  secondaryDisabled = false,
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="content-master-bulk-review-footer"
      role="toolbar"
      aria-label={t('content_master_bulk_review_footer_aria')}
    >
      <button
        type="button"
        className="content-master-bulk-review-footer__refresh"
        onClick={onRefresh}
        disabled={refreshing}
        title={t('content_master_bulk_review_footer_refresh')}
        aria-label={t('content_master_bulk_review_footer_refresh')}
      >
        <svg
          className="content-master-bulk-review-footer__refresh-icon"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
          <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
        </svg>
        <span className="content-master-bulk-review-footer__refresh-label">
          {t('content_master_bulk_review_footer_refresh')}
        </span>
      </button>
      {secondaryLabel && onSecondary ? (
        <button
          type="button"
          className="content-master-bulk-bar__link"
          onClick={onSecondary}
          disabled={secondaryDisabled}
        >
          {secondaryLabel}
        </button>
      ) : null}
      {primaryLabel && onPrimary ? (
        <button
          type="button"
          className="content-master-bulk-bar__primary"
          onClick={onPrimary}
          disabled={primaryDisabled}
          title={primaryTitle}
        >
          {primaryLabel}
        </button>
      ) : null}
    </div>
  );
};

export default ContentMasterReviewFooter;
